/**
 * VaultBackupManager — encrypted vault backup via CryptoBlobStore.
 *
 * Uses the same commit pipeline as workspace/registry blobs:
 * - buildSignedCommit() for §5.2-compliant commits (writers[], sig, canonical JSON)
 * - encryptState()/decryptState() for state encryption
 * - packFileContent()/resolveContentBytes() for file content
 *
 * Key derivation chain:
 *   credentialId → HKDF → { blobId, readKey, readerSeed, writerSeed }
 *   readKey + blobId → HKDF → { stateKey, fileKey }
 */

import { sha256 as nobleSha256 } from '@noble/hashes/sha256'
import { hkdf } from '@noble/hashes/hkdf'
import { bytesToHex } from '@noble/hashes/utils'
import { getPublicKeyAsync } from '@noble/ed25519'
import type { RemoteStore } from './types'
import { encodeBase64Url } from './identity-core'
import { buildSignedCommit } from './commitSigning'
import { HttpRemoteStore, WebCryptoProvider } from './providers'
import { encryptState, decryptState, buildAad, type WorkspaceState } from './stateCodec'
import { packFileContent, resolveContentBytes, type ContentCodecDeps } from './contentCodec'
import { buildTreeFromPaths, resolvePath, type DirEntryFile } from './dirTreeCodec'
import {
    bootstrapWorkspace,
    createWorkspaceAuthProvider,
} from '@secureblob/sdk/workspaceAuth'
import {
    encodeWorkspaceSecretV2,
    type WorkspaceSecretV2,
} from '@secureblob/sdk/workspaceSecretV2'

const encoder = new TextEncoder()

// ── Key derivation ─────────────────────────────────────────────

/** Derive workspace-scoped encryption key (matches configBlobSync / realWorkspace). */
function deriveKey(readKey: Uint8Array, blobId: string, scope: 'state' | 'file'): Uint8Array {
    const salt = encoder.encode(`secureblob:${blobId}`)
    const info = encoder.encode(`secureblob-${scope}-v1`)
    return hkdf(nobleSha256, readKey, salt, info, 32)
}

/** Coerce any Uint8Array to one backed by a standalone ArrayBuffer. */
function toAB(src: Uint8Array): Uint8Array<ArrayBuffer> {
    if (src.buffer instanceof ArrayBuffer && src.byteOffset === 0 && src.byteLength === src.buffer.byteLength) {
        return src as Uint8Array<ArrayBuffer>
    }
    return new Uint8Array(src) as Uint8Array<ArrayBuffer>
}

// ── Types ──────────────────────────────────────────────────────

export interface VaultBackupInfo {
    blobId: string
    readKey: Uint8Array
    readerSeed: Uint8Array
    writerSeed: Uint8Array
    writerKid: string
    writerPk: Uint8Array
    secret: WorkspaceSecretV2
    workspaceSecret: string
}

// ── VaultBackupManager ─────────────────────────────────────────

export class VaultBackupManager {
    private crypto = new WebCryptoProvider()
    /** In-flight push promise for coalescing concurrent pushBackup calls (§4.9.1) */
    private inflight: Promise<void> | null = null
    private bootstrapPromises = new Map<string, Promise<void>>()

    constructor(
        private readonly remote: RemoteStore,
        private readonly apiBaseUrl?: string,
    ) { }

    async deriveBackupInfo(credentialId: Uint8Array): Promise<VaultBackupInfo> {
        const ikm = nobleSha256(credentialId)
        const salt = new Uint8Array(0)

        const vaultNodeSeed = hkdf(nobleSha256, ikm, salt, encoder.encode('secureblob:vault-backup-seed'), 32)

        const blobIdRaw = hkdf(nobleSha256, vaultNodeSeed, salt, encoder.encode('secureblob:vault-blob-id'), 32)
        const blobId = bytesToHex(nobleSha256(blobIdRaw))

        const readKey = hkdf(nobleSha256, vaultNodeSeed, salt, encoder.encode('secureblob:vault-read-key'), 32)
        const readerSeed = hkdf(nobleSha256, vaultNodeSeed, salt, encoder.encode('secureblob:vault-reader-seed-v2'), 32)
        const writerSeed = hkdf(nobleSha256, vaultNodeSeed, salt, encoder.encode('secureblob:vault-writer-seed'), 32)

        const writerPk = await getPublicKeyAsync(writerSeed)
        const writerKid = encodeBase64Url(nobleSha256(writerPk).slice(0, 16))

        const secret: WorkspaceSecretV2 = {
            v: 2,
            type: 'workspace-secret',
            blobId,
            readKey: encodeBase64Url(readKey),
            readerSeed: encodeBase64Url(readerSeed),
            writerSeed: encodeBase64Url(writerSeed),
        }
        const workspaceSecret = encodeWorkspaceSecretV2(secret)

        return { blobId, readKey, readerSeed, writerSeed, writerKid, writerPk, secret, workspaceSecret }
    }

    // ── Push ───────────────────────────────────────────────────

    async pushBackup(credentialId: Uint8Array, vaultRecord: any): Promise<void> {
        // Coalesce: if a push is already in-flight, await it and skip ours.
        // The data is identical (same vault record), so the first push's
        // result is valid for all concurrent callers.
        if (this.inflight) {
            console.log(`[VaultBackup] pushBackup coalesced — awaiting in-flight push`)
            await this.inflight
            return
        }

        this.inflight = this.pushBackupInternal(credentialId, vaultRecord)
        try {
            await this.inflight
        } finally {
            this.inflight = null
        }
    }

    private async pushBackupInternal(credentialId: Uint8Array, vaultRecord: any): Promise<void> {
        const info = await this.deriveBackupInfo(credentialId)
        console.log(`[VaultBackup] pushBackup blob=${info.blobId.slice(0, 12)}`)
        const { stateKey, codecDeps } = this.deriveKeysAndCodec(info)
        const remote = this.getRemoteStore(info)

        // 1. Fetch current remote head (avoids unnecessary CAS_MISMATCH 409)
        //    Server requires base.length ≤ 1, so use only the latest head.
        const { heads: allHeads } = await this.getHeadsForPush(remote, info)
        const currentHead = allHeads[0] ?? null

        // 2. Encrypt file content
        const fileContent = encoder.encode(JSON.stringify(vaultRecord))
        const packed = await packFileContent(fileContent, codecDeps)

        // 3. Build v3 dir tree + workspace state
        const now = Date.now()
        const dirTreeDeps = { crypto: this.crypto, stateKey, blobId: info.blobId }
        const { rootDirHash, objects: dirObjects } = await buildTreeFromPaths(
            [{
                path: '/vault.json',
                entry: {
                    kind: 'file' as const,
                    mime: 'application/json',
                    size: fileContent.byteLength,
                    mtime: now,
                    ctime: now,
                    content: packed.content,
                },
            }],
            dirTreeDeps,
        )
        const wsState: WorkspaceState = { type: 'secureblob-state-v3', rootDirHash }
        const { ciphertext: stateBytes, hash: stateHash } = await encryptState(wsState, {
            crypto: this.crypto, stateKey, blobId: info.blobId,
        })

        // 4. Build signed commit with current head as parent (§5.2)
        const parents = currentHead ? [currentHead] : []
        const { commitBytes, commitHash } = await buildSignedCommit({
            blobId: info.blobId,
            parents,
            stateHash,
            writerSeed: toAB(info.writerSeed),
            crypto: this.crypto,
        })

        // 5. Push objects (dir tree + file content + state)
        for (const [hash, bytes] of dirObjects) {
            await remote.putObject(hash, bytes, undefined, undefined, info.blobId)
        }
        for (const obj of packed.objects) {
            await remote.putObject(obj.hash, obj.bytes, undefined, undefined, info.blobId)
        }
        await remote.putObject(stateHash, stateBytes, undefined, undefined, info.blobId)
        await remote.putObject(commitHash, commitBytes, undefined, undefined, info.blobId)

        // §7.4.4: Build referencedObjects for server-side GC
        const referencedObjects = [stateHash, ...[...dirObjects.keys()], ...packed.objects.map(o => o.hash)]

        // 6. Update head (CAS retry for concurrent modifications)
        await this.updateHeadWithRetry(remote, info, stateHash, commitHash, parents, referencedObjects)
    }

    // ── Pull ───────────────────────────────────────────────────

    async pullBackup(credentialId: Uint8Array): Promise<any | null> {
        const info = await this.deriveBackupInfo(credentialId)
        const remote = this.getRemoteStore(info)

        const { heads } = await this.getHeadsForPull(remote, info)
        if (heads.length === 0) return null

        const commitBytes = await remote.getObject(heads[0], undefined, undefined, info.blobId)
        if (!commitBytes) return null

        const commit = JSON.parse(new TextDecoder().decode(commitBytes))
        const stateHash = commit.payload?.stateHash
        if (!stateHash) return null

        const { stateKey, codecDeps } = this.deriveKeysAndCodec(info)
        const dirTreeDeps = { crypto: this.crypto, stateKey, blobId: info.blobId }

        try {
            // Decrypt state
            const stateCiphertext = await remote.getObject(stateHash, undefined, undefined, info.blobId)
            if (!stateCiphertext) return null

            const wsState = await decryptState(stateCiphertext, {
                crypto: this.crypto, stateKey, blobId: info.blobId,
            })
            if (!wsState.rootDirHash) return null

            // Resolve /vault.json via dir tree
            const dirStore = {
                get: (h: string) => remote.getObject(h, undefined, undefined, info.blobId).then(r => r ?? null),
                put: async () => { },
                has: async () => false,
            }
            const resolved = await resolvePath('/vault.json', wsState.rootDirHash, dirStore, dirTreeDeps)
            if (!resolved || resolved.entry.kind !== 'file') return null
            const vaultFile = resolved.entry as DirEntryFile

            // Decrypt file content
            const plaintext = await resolveContentBytes(
                vaultFile.content,
                {
                    load: async (h) => {
                        const obj = await remote.getObject(h, undefined, undefined, info.blobId)
                        if (!obj) throw new Error(`Object ${h.slice(0, 12)} not found`)
                        return obj
                    },
                    loadBatch: (hs) => remote.getObjectsBatch(hs, undefined, undefined, info.blobId),
                },
                codecDeps,
            )
            if (!plaintext) return null

            return JSON.parse(new TextDecoder().decode(plaintext))
        } catch (e) {
            console.warn(`VaultBackupManager: AEAD corruption in backup for ${info.blobId.slice(0, 12)} — returning null`, e)
            return null
        }
    }

    // ── Delete ─────────────────────────────────────────────────

    async deleteBackup(credentialId: Uint8Array): Promise<void> {
        const info = await this.deriveBackupInfo(credentialId)
        const remote = this.getRemoteStore(info)
        try {
            await remote.softDeleteBlob(info.blobId, undefined, undefined)
        } catch (error) {
            if (this.isAclMissingError(error)) return
            throw error
        }
    }

    // ── Internals ──────────────────────────────────────────────

    /** Derive state/file keys and build ContentCodecDeps for a backup blob. */
    private deriveKeysAndCodec(info: VaultBackupInfo): {
        stateKey: Uint8Array
        fileKey: Uint8Array
        codecDeps: ContentCodecDeps
    } {
        const stateKey = deriveKey(info.readKey, info.blobId, 'state')
        const fileKey = deriveKey(info.readKey, info.blobId, 'file')
        const fileAad = buildAad(info.blobId, 'file')

        return {
            stateKey,
            fileKey,
            codecDeps: {
                encryptRawObject: (pt) => this.crypto.encrypt(fileKey, pt, fileAad),
                decryptRawObject: (ct) => this.crypto.decrypt(fileKey, ct, fileAad),
                encryptDeterministic: (pt) => this.crypto.encryptDeterministic(fileKey, pt, fileAad),
                hasObject: async () => false, // vault blobs are small, no write-path dedup needed
                sha256: (data) => this.crypto.sha256(data),
            },
        }
    }
    /**
     * Update head with CAS retry (§8.2, §8.3).
     *
     * Server is single-head mode (§9): 0 or 1 head per blob.
     *
     * Flow:
     * 1. Try updateHead(base=parents, new=commitHash)
     * 2. CAS_MISMATCH (§8.3) → race detected, remote head changed since getHeads.
     *    Rebuild commit with remoteHead as parent (§8.2.5) → retry once.
     * 3. OBJECT_MISSING → server lost objects. Retry as bootstrap (base=[]).
     */
    private async updateHeadWithRetry(
        remote: RemoteStore,
        info: VaultBackupInfo, stateHash: string,
        commitHash: string, parents: string[],
        referencedObjects?: string[],
    ): Promise<void> {
        let res = await remote.updateHead(info.blobId, parents, commitHash, undefined, undefined, referencedObjects)

        // CAS_MISMATCH: another client pushed between getHeads and updateHead (§8.3)
        if (!res.ok && res.code === 'CAS_MISMATCH') {
            const remoteHead = 'remoteHead' in res ? res.remoteHead as string : ''
            if (remoteHead) {
                // Rebuild commit with remote head as parent (§8.2.5)
                const { commitBytes: cb, commitHash: ch } = await buildSignedCommit({
                    blobId: info.blobId,
                    parents: [remoteHead],
                    stateHash,
                    writerSeed: toAB(info.writerSeed),
                    crypto: this.crypto,
                })
                await remote.putObject(ch, cb, undefined, undefined, info.blobId)
                res = await remote.updateHead(info.blobId, [remoteHead], ch, undefined, undefined, referencedObjects)
            }
        }

        // OBJECT_MISSING: server lost commit/parent objects. Retry as bootstrap (§8.2.4)
        if (!res.ok && res.code === 'OBJECT_MISSING') {
            const { commitBytes: cb, commitHash: ch } = await buildSignedCommit({
                blobId: info.blobId,
                parents: [],
                stateHash,
                writerSeed: toAB(info.writerSeed),
                crypto: this.crypto,
            })
            await remote.putObject(ch, cb, undefined, undefined, info.blobId)
            res = await remote.updateHead(info.blobId, [], ch, undefined, undefined, referencedObjects)
        }

        if (!res.ok) {
            throw new Error(`Failed to update vault backup head: ${res.code}`)
        }
    }

    private getRemoteStore(info: VaultBackupInfo): RemoteStore {
        if (!this.apiBaseUrl) return this.remote
        const authProvider = createWorkspaceAuthProvider(this.apiBaseUrl, info.workspaceSecret, info.blobId) ?? undefined
        return new HttpRemoteStore(this.apiBaseUrl, info.workspaceSecret, info.blobId, authProvider)
    }

    private isAclMissingError(error: unknown): boolean {
        return error instanceof Error && error.message.includes('ACL_NOT_FOUND')
    }

    private async ensureBootstrapped(info: VaultBackupInfo): Promise<void> {
        const apiBaseUrl = this.apiBaseUrl
        if (!apiBaseUrl) return
        let promise = this.bootstrapPromises.get(info.blobId)
        if (!promise) {
            promise = (async () => {
                console.info('[VaultBackup] bootstrap:start', { blobId: info.blobId })
                await bootstrapWorkspace(apiBaseUrl, info.secret)
                console.info('[VaultBackup] bootstrap:ready', { blobId: info.blobId })
            })().finally(() => {
                this.bootstrapPromises.delete(info.blobId)
            })
            this.bootstrapPromises.set(info.blobId, promise)
        }
        await promise
    }

    private async getHeadsForPush(remote: RemoteStore, info: VaultBackupInfo) {
        try {
            return await remote.getHeads(info.blobId)
        } catch (error) {
            if (!this.isAclMissingError(error)) throw error
            await this.ensureBootstrapped(info)
            return await remote.getHeads(info.blobId)
        }
    }

    private async getHeadsForPull(remote: RemoteStore, info: VaultBackupInfo) {
        try {
            return await remote.getHeads(info.blobId)
        } catch (error) {
            if (this.isAclMissingError(error)) {
                console.info('[VaultBackup] pull:no-acl', { blobId: info.blobId })
                return { heads: [] }
            }
            throw error
        }
    }
}
